export class Carousel {
  slidesContainer: HTMLElement
  activeSlide: number = 0
  scrollObserver?: IntersectionObserver

  constructor(slidesContainer: HTMLElement) {
    this.slidesContainer = slidesContainer
  }

  /**
   * Create an IntersectionObserver that will update the active
   * tab in the navbar when the user scrolls.
   */
  observeScroll(): void {
    if (window.__PRERENDER_INJECTED) {
      // IntersectionObserver doesn't exist in JSDOM.
      return
    }

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) {
          return
        }
        const slide = entries[0].target as HTMLElement
        const slideIndex = Number(slide.dataset.order) || 0
        this.activeSlide = slideIndex
        this.updateActiveSlideDOM()
      },
      { threshold: 1, root: this.slidesContainer },
    )

    const slides = this.slidesContainer.children
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i] as HTMLElement
      slide.dataset.order = String(i)
      this.scrollObserver.observe(slide)
    }
  }

  /**
   * Stop all event listeners and remove references to DOM Nodes.
   */
  unmount(): void {
    this.scrollObserver?.disconnect()
    this.scrollObserver = undefined
    // @ts-ignore Type 'undefined' is not assignable to type 'HTMLElement'.
    this.slidesContainer = undefined
  }

  scrollTo(index: number): void {
    const width = this.slidesContainer.offsetWidth
    this.slidesContainer.scroll({ left: width * index })

    this.activeSlide = index
    this.updateActiveSlideDOM()
  }

  next(): void {
    const nextSlide = this.activeSlide + 1
    if (nextSlide < this.maxSlides) {
      this.scrollTo(nextSlide)
    }
  }

  prev(): void {
    const prevSlide = this.activeSlide - 1
    if (prevSlide >= 0) {
      this.scrollTo(prevSlide)
    }
  }

  get maxSlides(): number {
    return this.slidesContainer.children.length
  }

  protected updateActiveSlideDOM(): void {
    // Not implemented.
  }
}

/**
 * An extension of the Carousel logic that updates the DOM,
 * for use in pre-rendered pages.
 */
class CarouselStaticPages extends Carousel {
  carouselContainer: HTMLElement

  constructor(carouselContainer: HTMLElement) {
    const slidesContainer =
      carouselContainer.querySelector<HTMLElement>('.carousel__slides')!

    super(slidesContainer)
    this.carouselContainer = carouselContainer
    this.observeScroll()
  }

  init(): void {
    if (this.navButtons) {
      for (let i = 0; i < this.navButtons.length; i++) {
        const button = this.navButtons[i] as HTMLElement
        button.addEventListener('click', () => this.scrollTo(i))
      }
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next())
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev())
    }
  }

  protected updateActiveSlideDOM(): void {
    if (this.activeNavButton) {
      this.activeNavButton.dataset.active = 'false'
    }

    if (this.navButtons?.[this.activeSlide]) {
      this.navButtons[this.activeSlide].dataset.active = 'true'
    }
  }

  get nav(): HTMLElement | null {
    return this.carouselContainer.querySelector('.carousel__nav')
  }

  get navButtons(): HTMLCollectionOf<HTMLElement> | undefined {
    return this.nav?.children as HTMLCollectionOf<HTMLElement>
  }

  get activeNavButton(): HTMLElement | null | undefined {
    return this.nav?.querySelector('[data-active="true"]')
  }

  get prevButton(): HTMLElement | null {
    return this.carouselContainer.querySelector('.carousel__button--prev')
  }

  get nextButton(): HTMLElement | null {
    return this.carouselContainer.querySelector('.carousel__button--next')
  }
}

export function initCarouselStaticPages(): void {
  if (window.__PRERENDER_INJECTED) {
    return
  }

  const carousels = document.querySelectorAll<HTMLElement>('.carousel')
  carousels.forEach((carousel) => {
    const view = new CarouselStaticPages(carousel)
    view.init()
  })
}
